<script setup>
import { ref, reactive, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { viewportSize, getCurrentRotation, getCurrentTranslate, getCurrentScale } from './utils';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

/////////////////////////////////////// Props
const props = defineProps({
  src: {
    type: String,
    default: null,
    required: true
  },
  bgColor: {
    type: String,
    default: "#ffffff",
    required: false
  },
  parallax: {
    type: Boolean,
    default: null,
    required: false
  },
  styles: {
    type: Object,
    default: null,
    required: false
  },
  loader: {
    // type: Object,
    required: false,
    default: null,
  },
  overlay: {
    type: String,
    required: false,
    default: null,
  },
  pattern: {
    type: String,
    required: false,
    default: null,
  },
  effect: {
    type: String,
    required: false,
    default: null, // height, zoom
  },
  lazyLoad: {
    type: Boolean,
    required: false,
    default: true,
  },
  edit: {
    type: Boolean,
    default: false,
    required: false
  },
  breakpoints: {
    type: Object,
    default: (window.Laravel.available_breakpoints ?? null),
    required: false
  },
  saving: {
    type: Boolean,
    default: false,
    required: false
  },
  debug: {
    type: Boolean,
    default: false,
    required: false
  },
});

/////////////////////////////////////// Emits
const emit = defineEmits(["loaded", "save"]);

/////////////////////////////////////// Init component
///////////////////////// Wrapper
const wrapper = ref();
const wrapperDimensions = reactive({});

onMounted(() => {
  Object.assign(wrapperDimensions, {
    width: wrapper.value.offsetWidth,
    height: wrapper.value.offsetHeight,
    ratio: wrapper.value.offsetWidth / wrapper.value.offsetHeight
  });
});

///////////////////////// Image
let loaded = ref(false);
const image = ref();
const imageDimensions = reactive({});

const onLoaded = () => {
  Object.assign(imageDimensions, {
    width: image.value.naturalWidth,
    height: image.value.naturalHeight,
    ratio: image.value.naturalWidth / image.value.naturalHeight
  });

  if (!props.styles) {
    if (wrapperDimensions.ratio > imageDimensions.ratio) {
      fitImage('width');
    } else {
      fitImage('height');
    }
  }

  loaded.value = true;

  emit('loaded');
};

/////////////////////////////////////// Update viewport and wrapper on resize
const viewport = reactive(viewportSize());

const resizeListener = (e) => {
  Object.assign(viewport, viewportSize());

  Object.assign(wrapperDimensions, {
    width: wrapper.value.offsetWidth,
    height: wrapper.value.offsetHeight,
    ratio: wrapper.value.offsetWidth / wrapper.value.offsetHeight
  });
};

window.addEventListener("resize", resizeListener, {
  passive: true
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', resizeListener);
});

/////////////////////////////////////// Init styles and breakpoints
// keep initStyles somewhere for discard
const initStyles = ref({ ...props.styles } ?? { "0": { bgColor: props.bgColor } });

// init tempStyles if there are styles property
const tempStyles = reactive(JSON.parse(JSON.stringify(props.styles)) ?? { "0": { bgColor: props.bgColor } });

// add keys to tempStyles for empty breakpoints
if (props.breakpoints) {
  const tempStylesKeys = [];

  Object.keys(tempStyles).forEach(key => {
    tempStylesKeys.push(key);
  });

  Object.keys(props.breakpoints).forEach(key => {
    if (!tempStylesKeys.includes(key)) {
      tempStyles[`${props.breakpoints[key].min}-${props.breakpoints[key].max}`] = {};
    }
  });
};

// get stylesKey for currentViewport
const currentStylesKey = computed(() => {
  let currentKey = "0";

  if (props.breakpoints) {
    Object.keys(props.breakpoints).forEach(key => {
      if (viewport.width >= props.breakpoints[key].min && viewport.width <= props.breakpoints[key].max) {
        currentKey = key;
      }
    });
  }

  return currentKey;
});

// get tempStyles for currentViewport
const currentTempStyles = computed(() => {
  return tempStyles[currentStylesKey.value] ?? { bgColor: "#000000" };
});

/////////////////////////////////////// Edit mode
const editable = ref(false);

const setTransform = (options) => {
  const currentTranslate = getCurrentTranslate(image.value);
  const currentRotate = getCurrentRotation(image.value);
  const currentScale = getCurrentScale(image.value);

  let newTranslate = { x: parseInt(currentTranslate.x), y: parseInt(currentTranslate.y) };
  let newRotate = currentRotate;
  let newScale = currentScale.toFixed(2);

  if (options.translate) {
    newTranslate.x = parseInt(options.translate.x);
    newTranslate.y = parseInt(options.translate.y);
  }

  if (typeof options.rotate === 'number') {
    newRotate = options.rotate;
  }

  if (options.scale) {
    newScale = parseFloat(options.scale).toFixed(2);
  }

  tempStyles[currentStylesKey.value].transform = `translate(${newTranslate.x}%, ${newTranslate.y}%) rotate(${newRotate}deg) scale(${newScale})`;
}

const updateTransform = (options) => {
  const currentTranslate = getCurrentTranslate(image.value);
  const currentRotate = getCurrentRotation(image.value);
  const currentScale = getCurrentScale(image.value);

  let newTranslate = { x: parseInt(currentTranslate.x), y: parseInt(currentTranslate.y) };
  let newRotate = currentRotate;
  let newScale = currentScale.toFixed(2);

  if (options.translate) {
    newTranslate.x = parseInt(currentTranslate.x) + options.translate.x;
    newTranslate.y = parseInt(currentTranslate.y) + options.translate.y;
  }

  if (typeof options.rotate === 'number') {
    newRotate = currentRotate + options.rotate;
  }

  if (options.scale) {
    newScale = parseFloat(currentScale + options.scale).toFixed(2);
  }

  tempStyles[currentStylesKey.value].transform = `translate(${newTranslate.x}%, ${newTranslate.y}%) rotate(${newRotate}deg) scale(${newScale})`;
}

const fitImage = (dimension) => {
  if (dimension === 'width') {
    tempStyles[currentStylesKey.value].height = "";
    setTimeout(() => {
      delete tempStyles[currentStylesKey.value].height;
    }, 0);
    tempStyles[currentStylesKey.value].width = "100%";
  }

  if (dimension === 'height') {
    tempStyles[currentStylesKey.value].width = "";
    setTimeout(() => {
      delete tempStyles[currentStylesKey.value].width;
    }, 0);
    tempStyles[currentStylesKey.value].height = "100%";
  }

  setTransform({ scale: 1, rotate: 0, translate: { x: -50, y: -50 } });
};

const downListener = (e) => {
  e.preventDefault();

  const mouseStart = {
    x: e.clientX,
    y: e.clientY
  };

  const currentTranslate = getCurrentTranslate(image.value);

  const moveListener = (e) => {
    let mousePos, movePercentage, zoomLevel, actualMovePercentage, newBgPosition;

    mousePos = {
      x: e.clientX,
      y: e.clientY
    }

    if (mouseStart !== mousePos) {
      movePercentage = {
        x: (100 * (mousePos.x - mouseStart.x)) / wrapper.value.offsetWidth,
        y: (100 * (mousePos.y - mouseStart.y)) / wrapper.value.offsetHeight
      };

      // zoomLevel = 90;

      // actualMovePercentage = {
      //   x: (0.1 / (1 - (zoomLevel / 100))) * movePercentage.x,
      //   y: (0.1 / (1 - (zoomLevel / 100))) * movePercentage.y
      // };

      setTransform({ translate: { x: currentTranslate.x + movePercentage.x, y: currentTranslate.y + movePercentage.y } });
    }
  }

  document.addEventListener('mouseup', function (e) {
    document.removeEventListener('mousemove', moveListener);
  });

  document.addEventListener('mousemove', moveListener);
};

const wheelListener = (e) => {
  e.preventDefault();

  if (e.deltaY > 0) {
    updateTransform({ scale: -0.1 })
  } else {
    updateTransform({ scale: 0.1 })
  }
};

if (props.edit) {
  onMounted(() => {
    image.value.addEventListener('mousedown', downListener);
    image.value.addEventListener('wheel', wheelListener);
  });

  onBeforeUnmount(() => {
    image.value.removeEventListener('mousedown', downListener);
    image.value.removeEventListener('wheel', downListener);
  });
};

/////////////////////////////////////// Save - Discard
const actionsEnabled = computed(() => {
  if (JSON.stringify(tempStyles) !== JSON.stringify(initStyles.value)) {
    return true;
  }

  return false;
});

const save = () => {
  emit('save', { styles: tempStyles });
  editable.value = false;
};

const discard = () => {
  const initStylesValue = JSON.parse(JSON.stringify(initStyles.value));
  Object.assign(tempStyles, initStylesValue);
  editable.value = false;
};

watch(() => props.saving, (newValue, oldValue) => {
  if (oldValue && !newValue) {
    const tempStylesValue = JSON.parse(JSON.stringify(tempStyles))

    if (initStyles.value) {
      Object.assign(initStyles.value, tempStylesValue);
    } else {
      initStyles.value = tempStylesValue;
    }

    editable.value = false;
  }
});

/////////////////////////////////////// Lazy Load
if (props.lazyLoad) {
  const loadObserver = new IntersectionObserver((entries) => {
    const entry = entries[0];
    if (entry.isIntersecting) {

      if (!props.edit) {
        image.value.setAttribute("src", image.value.getAttribute("fake-src"));
      }

      loadObserver.disconnect();
    }
  }, {
    threshold: 0,
  });

  onMounted(() => {
    loadObserver.observe(wrapper.value);
  });
};

/////////////////////////////////////// Height effect
const effectHelper = ref();

if (props.effect) {
  const effectObserver = new IntersectionObserver((entries) => {
    const entry = entries[0];
    if (entry.isIntersecting) {
      effectHelper.value.classList.add("effect-active");
      effectObserver.disconnect();
    }
  }, {
    threshold: 0,
  });

  onMounted(() => {
    effectObserver.observe(wrapper.value);
  });
};

///////////////////////// Parallax
if (props.parallax) {
  setTimeout(() => {
    gsap.to(wrapper.value, {
      scrollTrigger: {
        scrub: 2,
        // invalidateOnRefresh: true
      },
      yPercent: (i, target) => -20,
      ease: "none"
    });
  }, 0);

  // window.addEventListener("scroll", function () {
  //   const distance = window.scrollY;

  //   setTimeout(() => {
  //     wrapper.value.style.transform = `translateY(${distance * 1}px)`
  //   }, 10)
  // });
}
</script>

<template>
  <div v-if="edit" class="image-editor" :class="{ 'small': wrapperDimensions.width < 265 || wrapperDimensions.height < 315, 'protected': !editable }">
    <div ref="wrapper" class="image-wrapper" :class="{ 'loaded': loaded }" :style="{ 'background-color': currentTempStyles.bgColor }">
      <img ref="image" :src="src" class="image" :style="currentTempStyles" @load="onLoaded()" />
    </div>

    <div class="actions-wrapper">
      <button type="button" class="btn-action btn-discard" @click="discard()">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 468 468">
          <path fill="#ff0000" fill-rule="evenodd" d="M10.43 10.43c13.017-13.018 34.123-13.018 47.14 0L234 186.86 410.43 10.43c13.017-13.018 34.123-13.018 47.14 0 13.018 13.017 13.018 34.123 0 47.14L281.14 234l176.43 176.43c13.018 13.017 13.018 34.123 0 47.14-13.017 13.018-34.123 13.018-47.14 0L234 281.14 57.57 457.57c-13.017 13.018-34.123 13.018-47.14 0-13.018-13.017-13.018-34.123 0-47.14L186.86 234 10.43 57.57c-13.018-13.017-13.018-34.123 0-47.14Z" clip-rule="evenodd" />
        </svg>
      </button>

      <button v-if="actionsEnabled" type="button" class="btn-action btn-save" @click="save()">
        <svg v-if="!saving" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 434">
          <path fill="#39cc00" fill-rule="evenodd" d="M590.319 10.179c12.972 13.062 12.898 34.168-.164 47.14L220.924 423.986c-12.998 12.908-33.978 12.908-46.976 0L9.845 261.022c-13.062-12.972-13.136-34.077-.164-47.14 12.972-13.063 34.078-13.136 47.14-.164l140.615 139.638L543.179 10.015c13.062-12.973 34.168-12.899 47.14.164Z" clip-rule="evenodd" />
        </svg>
        <span v-else class="btn-loader"></span>
      </button>
    </div>

    <div class="tools-wrapper">
      <div class="tools tools-top">
        <input type="color" class="tool-color" v-model="currentTempStyles.bgColor">

        <button type="button" class="tool" @click="setTransform({ translate: { x: -50, y: -50 } })">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <path stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 4H6a2 2 0 0 0-2 2v2m4 12H6a2 2 0 0 1-2-2v-2M16 4h2a2 2 0 0 1 2 2v2m-4 12h2a2 2 0 0 0 2-2v-2m-5-4a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
          </svg>
        </button>

        <button type="button" class="tool" @click="fitImage('width')">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <path fill="#000" d="M3 18.25a.75.75 0 0 0 1.5 0V5.75a.75.75 0 0 0-1.5 0v12.5Zm16.5 0a.75.75 0 0 0 1.5 0V5.75a.75.75 0 0 0-1.5 0v12.5Zm-5.354-4.197a.75.75 0 0 0 1.133.977l2.37-2.367a.77.77 0 0 0 .351-.664.77.77 0 0 0-.35-.664l-2.37-2.367-.085-.073a.75.75 0 0 0-.976.073l-.073.084a.75.75 0 0 0 .073.976l1.221 1.222h-3.784l-.025.002H8.56l1.221-1.222.08-.094a.75.75 0 0 0-.007-.882L9.78 8.97l-.094-.08a.75.75 0 0 0-.882.007l-.085.073-2.37 2.367-.074.053A.786.786 0 0 0 6 12a.77.77 0 0 0 .35.665l2.37 2.367.085.073a.75.75 0 0 0 1.049-1.05l-.073-.084-1.221-1.22h3.784l.025-.001h3.071l-1.221 1.22-.073.083Z" />
          </svg>
        </button>

        <button type="button" class="tool" @click="fitImage('height')">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <path fill="#000" d="M5.75 3a.75.75 0 0 0 0 1.5h12.5a.75.75 0 0 0 0-1.5H5.75Zm3.472 11.219a.75.75 0 0 1 .976-.073l.084.073 1.22 1.221v-3.07l-.002-.026V8.56l-1.22 1.221-.084.073a.75.75 0 0 1-1.049-1.05l.073-.084 2.367-2.37a.77.77 0 0 1 .664-.35c.252 0 .475.109.611.276l.053.075 2.367 2.37.073.084a.75.75 0 0 1 .007.882l-.08.094-.084.073a.75.75 0 0 1-.883.007l-.094-.08L13 8.56v3.07l.002.026v3.784l1.221-1.221a.75.75 0 0 1 .977-.073l.084.073a.75.75 0 0 1 .072.976l-.072.085-2.367 2.37a.77.77 0 0 1-.664.35.77.77 0 0 1-.664-.35l-2.367-2.37a.75.75 0 0 1 0-1.061ZM5 20.25a.75.75 0 0 1 .75-.75h12.5a.75.75 0 0 1 0 1.5H5.75a.75.75 0 0 1-.75-.75Z" />
          </svg>
        </button>
      </div>

      <div class="tools tools-right">
        <button type="button" class="tool" @click="updateTransform({ scale: -0.05 })">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <path stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m14.264 15.938-1.668-1.655c-.805-.798-1.208-1.197-1.67-1.343a2 2 0 0 0-1.246.014c-.458.156-.852.563-1.64 1.379L4.045 18.28m10.22-2.342.341-.339c.806-.8 1.21-1.199 1.671-1.345a2 2 0 0 1 1.248.015c.458.156.852.565 1.64 1.383l.836.842m-5.736-.556 4.011 4.019M20.999 6h-6M11 4H7.2c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C4 5.52 4 6.08 4 7.2v9.6c0 .658 0 1.122.044 1.48M20 10v6.494M4.044 18.28c.031.252.084.451.174.628a2 2 0 0 0 .874.874C5.52 20 6.08 20 7.2 20h9.6c1.12 0 1.68 0 2.108-.218a2 2 0 0 0 .874-.874C20 18.48 20 17.92 20 16.8v-.306" />
          </svg>
        </button>

        <button type="button" class="tool" @click="updateTransform({ scale: 0.05 })">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <path stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="m14.265 15.938-1.718-1.703c-.789-.783-1.184-1.175-1.638-1.321a2 2 0 0 0-1.23 0c-.454.146-.849.538-1.638 1.32l-4 4.028m10.224-2.324.341-.339c.806-.8 1.209-1.199 1.671-1.345a2 2 0 0 1 1.247.015c.46.156.853.565 1.64 1.383L20 16.5m-5.735-.562 3.955 4.025m0 0c-.35.037-.799.037-1.42.037H7.2c-1.12 0-1.68 0-2.108-.218a2 2 0 0 1-.874-.874 1.869 1.869 0 0 1-.176-.646m14.178 1.7c.28-.03.498-.083.688-.18a2 2 0 0 0 .874-.874C20 18.48 20 17.92 20 16.8V13m-9-9H7.2c-1.12 0-1.68 0-2.108.218a2 2 0 0 0-.874.874C4 5.52 4 6.08 4 7.2v9.6c0 .647 0 1.107.042 1.462M18 9V6m0 0V3m0 3h3m-3 0h-3" />
          </svg>
        </button>

        <button type="button" class="tool" @click="updateTransform({ rotate: 5 })">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <path stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.5 20.5a8.5 8.5 0 1 1 7.37-4.262M22.5 15l-3.63 1.238m-1.695-3.855 1.354 3.971.34-.116" />
          </svg>
        </button>

        <button type="button" class="tool" @click="updateTransform({ rotate: -5 })">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <path stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12.5 20.5a8.5 8.5 0 1 0-7.37-4.262M1.5 15l3.63 1.238m1.695-3.855-1.354 3.971-.34-.116" />
          </svg>
        </button>
      </div>
    </div>

    <div v-if="!editable" class="editable-protector" @click="editable = true"></div>

    <div v-if="debug" class="debugger">
      <p v-for="data in tempStyles">{{ data }}</p>
    </div>

    <div class="editor-content">
      <slot></slot>
    </div>
  </div>

  <div v-else ref="wrapper" class="image-wrapper" :class="{ 'loaded': loaded, 'parallax' : parallax }" :style="{ 'background-color': currentTempStyles.bgColor ?? null }">
    <span v-if="loader && !loaded" class="image-loader" :style="{ borderColor: loader.bg, borderLeftColor: loader.color, width: loader.size + 'px', height: loader.size + 'px' }"></span>

    <div ref="effectHelper" class="effect-helper" :class="[`effect-${effect}`]">
      <img ref="image" :src="!lazyLoad ? src : null" :fake-src="lazyLoad ? src : null" class="image" :style="currentTempStyles" @load="onLoaded()" />
    </div>

    <span v-if="overlay" class="overlay" :style="{ backgroundColor: overlay }"></span>
    <span v-if="pattern" class="pattern" :style="{ backgroundImage: `url(${pattern})` }"></span>
    <slot></slot>
  </div>
</template>

<style lang="scss">
@keyframes img-loader {
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }

  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

@keyframes btn-loader {
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }

  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

.image-wrapper {
  // overflow: hidden;
  width: 100%;
  height: 100%;
  position: relative;

  .image {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    opacity: 0;
    transition: opacity 150ms ease-in-out;
  }

  .image-loader {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) translateZ(0);
    border-radius: 50%;
    width: 60px;
    height: 60px;
    border-color: #fff;
    border-width: 2px;
    border-style: solid;
    border-left: 2px solid #000;
    animation: img-loader 1.1s infinite linear;
  }

  .overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
  }

  .pattern {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 100%;
  }

  .effect-helper {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;

    &.effect-height {
      clip-path: polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%);
      transition: clip-path 1000ms ease-out;
    }

    &.effect-zoom {
      transform: scale(1);
      transition: transform 7000ms ease-out;
    }
  }

  &.parallax {
    height: 120%;
  }

  &.bottom {
    .image {
      object-position: 50% 100% !important;
    }
  }

  &.left {
    .image {
      object-position: 0% 50% !important;
    }
  }

  &.cover {
    .image {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &.loaded {
    .image {
      opacity: 1;
    }

    .effect-helper {
      &.effect-active {
        &.effect-height {
          clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%);
        }

        &.effect-zoom {
          transform: scale(1.05);
        }
      }
    }
  }
}

.image-editor {
  position: relative;
  width: 100%;
  height: 100%;

  .editable-protector {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: rgba(#fff, 0.5);
    cursor: no-drop;
    transition: background-color 250ms ease-in-out;

    &:hover {
      background-color: rgba(#fff, 0);
    }
  }

  .actions-wrapper {
    position: absolute;
    bottom: calc(100% + 10px);
    right: 0;
    z-index: 1;

    .btn-action {
      background-color: #fff;
      border: none;
      width: 50px;
      height: 50px;
      border-radius: 50%;
      position: relative;
      margin-left: 10px;
      box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.3);
      cursor: pointer;
      transition: box-shadow 250ms ease-in-out;

      &:nth-child(1) {
        margin-left: 0px;
      }

      svg,
      img {
        width: 50%;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }

      .btn-loader {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%) translateZ(0);
        border-radius: 50%;
        width: 100%;
        height: 100%;
        border-color: #fff;
        border-width: 2px;
        border-style: solid;
        border-left: 2px solid #000;
        animation: btn-loader 1.1s infinite linear;
      }

      &:hover {
        box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
      }

      &.btn-discard {
        svg {
          width: 37%;
        }
      }
    }
  }

  .tools-wrapper {
    z-index: 1;
    position: absolute;
    top: 0;
    right: 0;
    opacity: 0;
    padding-top: 15px;
    padding-right: 15px;
    transition: opacity 250ms ease-in-out;

    .tools {
      position: absolute;
      display: flex;
      align-items: center;

      .tool {
        background-color: #fff;
        border: none;
        width: 50px;
        height: 50px;
        border-radius: 50%;
        position: relative;
        box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.3);
        cursor: pointer;
        transition: box-shadow 250ms ease-in-out;

        svg,
        img {
          width: 50%;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }

        &:hover {
          box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
        }
      }

      .tool-color {
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        background-color: #fff;
        width: 50px;
        height: 50px;
        border: none;
        cursor: pointer;
        border-radius: 50%;

        &::-webkit-color-swatch {
          border-radius: 50%;
          border: 1px solid #000000;
        }

        &::-moz-color-swatch {
          border-radius: 50%;
          border: 1px solid #000000;
        }
      }

      &.tools-top {
        right: 100%;
        top: 100%;

        .tool {
          margin-left: 10px;
        }
      }

      &.tools-right {
        top: calc(100% + 60px);
        right: 100%;
        flex-flow: column;

        .tool {
          margin-bottom: 10px;
        }
      }
    }
  }

  .debugger {
    position: absolute;
    left: 100%;
    bottom: 30px;
    width: 170px;
    background-color: #f2f2f2;
    padding: 10px;
    font-size: 12px;
    opacity: 0;
    transition: opacity 250ms ease-in-out;

    p {
      margin: 5px 0px;
    }
  }

  .image-wrapper {
    .image {
      cursor: grab;

      &:active {
        cursor: grabbing;
      }
    }
  }

  .editor-content {
    pointer-events: none;
  }

  &.protected {

    .actions-wrapper,
    .tools-wrapper {
      display: none;
    }
  }

  &.small {
    .actions-wrapper {
      bottom: unset;
      top: 10px;
      right: 10px;
    }

    .tools-wrapper {
      top: unset;
      right: unset;
      bottom: 100%;
      left: 100%;

      .tools {
        &.tools-top {
          top: unset;
          bottom: 100%;
        }

        &.tools-right {
          right: unset;
          top: 0;
          left: 100%;
        }
      }
    }
  }

  &:not(.protected):hover {
    .image-wrapper {
      overflow: visible;

      &::after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        right: 0;
        border: 2px dashed #aaaaaa;
        pointer-events: none;
        z-index: 1;
      }
    }

    .tools-wrapper {
      opacity: 1;
    }

    .debugger {
      opacity: 1;
    }
  }
}
</style>